import { useEffect, useState } from 'react';

import type { GTMEventGlycerinSessionCartInformation } from '@packages/tracking';
import { useTracking } from '@packages/tracking';

/**
 * This hook is used to notify Glycerin of the cart information at the start of a new session.
 */
export const useSessionCartInfoTracking = ({
  isLoading,
  data,
}: {
  isLoading: boolean;
  data?: {
    size: number;
    subTotal: { currencyCode: string; value: number };
  };
}) => {
  const dispatchGtmEvent = useTracking();

  const [newSessionHasStarted, setNewSessionHasStarted] = useState(false);

  useEffect(() => {
    const scheduleEvent = () => {
      setNewSessionHasStarted(true);
    };

    document.addEventListener('window.glycerin.session.start', scheduleEvent);

    return () => {
      document.removeEventListener('window.glycerin.session.start', scheduleEvent);
    };
  }, []);

  useEffect(() => {
    if (isLoading) return;

    if (typeof window !== 'undefined') {
      window.basketCount = data?.size || 0;
    }

    if (!newSessionHasStarted) return;

    dispatchGtmEvent<GTMEventGlycerinSessionCartInformation>({
      event: 'SessionCartInformation',
      SessionCartInformationData:
        data && data.size > 0
          ? {
              persistentCart: true,
              currency:
                data.subTotal.currencyCode === 'EUR' || data.subTotal.currencyCode === 'CHF'
                  ? data.subTotal.currencyCode
                  : 'EUR',
              persistentValue: data.subTotal.value,
            }
          : {
              persistentCart: false,
            },
    });

    setNewSessionHasStarted(false);
  }, [data, isLoading, dispatchGtmEvent, newSessionHasStarted]);
};
